import { createContext, useContext, type ReactNode } from "react";
import type { DiagnosticListItem } from "admin/types/interfaces/diagnostics";

// Carries the session's diagnostics to the individual turns without threading
// props through the transcript hierarchy, which is shared with every other
// conversation surface and stays diagnostics-unaware.
export interface TurnDiagnosticsContextValue {
  /** Diagnostics keyed by root trace id, matching what a turn's anchor id resolves to. */
  byTurnId: Map<string, DiagnosticListItem>;
  /** 1-based position of each assistant turn in the transcript, keyed by anchor id. */
  ordinalByTurnId: Map<string, number>;
  totalTurns: number;
  /** Anchor id of the turn whose detail rail is open, or `null`. */
  selectedTurnId: string | null;
  selectTurn: (anchorId: string) => void;
}

const TurnDiagnosticsContext = createContext<TurnDiagnosticsContextValue | null>(null);

/**
 * N.B. mount this unconditionally and pass `null` to switch the layer off.
 * Conditionally wrapping the children changes the element type at that position,
 * which tears down and remounts the whole conversation view underneath it.
 */
export const TurnDiagnosticsProvider = ({
  value,
  children,
}: {
  value: TurnDiagnosticsContextValue | null;
  children: ReactNode;
}) => <TurnDiagnosticsContext.Provider value={value}>{children}</TurnDiagnosticsContext.Provider>;

/**
 * `null` wherever no provider is mounted or the layer is off - every surface
 * except the admin conversation view with diagnostics enabled - so per-turn
 * diagnostics UI renders nothing without each caller checking a flag.
 */
export const useTurnDiagnostics = (): TurnDiagnosticsContextValue | null =>
  useContext(TurnDiagnosticsContext);
